import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import { ArtefactCitation } from '../types';
import {
  canRenderCitation,
  getArtefactCitationSignature,
  getCitationDocumentName,
  getCitationDocumentReference,
  getCitationFileId,
  getCitationPageNumber,
  getCitationText,
  isCitationNavigationEnabled,
} from '../utils/citations';
import styles from './CaseIntelligenceDashboard.module.scss';

interface Props {
  citations?: ArtefactCitation[] | null;
  onCitationClick?: (citation: ArtefactCitation) => void;
  activeCitationSignature?: string | null;
}

const CaseIntelligenceCitationList: React.FC<Props> = ({
  citations = [],
  onCitationClick,
  activeCitationSignature = null,
}) => {
  const visibleCitations = React.useMemo(
    () =>
      (citations || []).filter(
        (citation): citation is ArtefactCitation => canRenderCitation(citation),
      ),
    [citations],
  );

  if (!visibleCitations.length) {
    return null;
  }

  return (
    <span className={styles.citationList}>
      {visibleCitations.map((citation, index) => {
        const citationSignature = getArtefactCitationSignature(citation);
        const pageNumber = getCitationPageNumber(citation);
        const citationText = getCitationText(citation) || `Source ${index + 1}`;
        const documentReference = getCitationDocumentReference(citation) || 'Unknown';
        const documentName = getCitationDocumentName(citation);
        const documentId = getCitationFileId(citation);
        const isEnabled = isCitationNavigationEnabled(citation);
        const isActive = activeCitationSignature === citationSignature;

        return (
          <Tooltip
            key={`${citationSignature}-${index}`}
            arrow
            placement="top"
            PopperProps={{
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'viewport',
                    padding: {
                      top: 8,
                      right: 10,
                      bottom: 8,
                      left: 8,
                    },
                  },
                },
              ],
            }}
            componentsProps={{
              tooltip: {
                className: styles.citationTooltip,
              },
            }}
            title={
              <div className={styles.citationPopover}>
                <div className={styles.citationPopoverText}>{citationText}</div>
                <div className={styles.citationPopoverMeta}>
                  {documentName ? (
                    <div>
                      <span className={styles.citationMetaLabel}>Document Name:</span>{' '}
                      {documentName}
                    </div>
                  ) : null}
                  {documentId ? (
                    <div>
                      <span className={styles.citationMetaLabel}>Document Id:</span> {documentId}
                    </div>
                  ) : !documentName ? (
                    <div>
                      <span className={styles.citationMetaLabel}>Document Id:</span>{' '}
                      {documentReference}
                    </div>
                  ) : null}
                  <div>
                    <span className={styles.citationMetaLabel}>Page Number:</span>{' '}
                    {pageNumber ?? 'Unknown'}
                  </div>
                </div>
              </div>
            }
          >
            <span className={styles.citationButtonWrap}>
              <button
                type="button"
                className={`${styles.citationLink} ${isActive ? styles.citationLinkActive : ''} ${
                  !isEnabled ? styles.citationLinkDisabled : ''
                }`}
                aria-disabled={!isEnabled}
                aria-pressed={isActive}
                tabIndex={isEnabled ? 0 : -1}
                aria-label={`Citation ${index + 1} for ${documentReference}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!isEnabled) {
                    return;
                  }
                  onCitationClick?.(citation);
                }}
              >
                [{index + 1}]
              </button>
            </span>
          </Tooltip>
        );
      })}
    </span>
  );
};

export default CaseIntelligenceCitationList;
